/**
 * InterviewListView Component
 * Displays a paginated list of AI interviews with status badges and quick actions.
 */

import { useState } from 'react'
import { Link } from 'react-router-dom'
import toast from 'react-hot-toast'
import {
  MicrophoneIcon,
  PlayIcon,
  ClockIcon,
  CheckCircleIcon,
  ExclamationCircleIcon,
  TrashIcon,
  ArrowDownTrayIcon,
  ArrowPathIcon,
  EyeIcon,
} from '@heroicons/react/24/outline'
import type { AIInterview, AIInterviewStatus, AIInterviewType } from '../../types/interview'
import { API_V2_URL, apiV2, aiInterviewsApi } from '../../services/api'

interface InterviewListViewProps {
  interviews: AIInterview[]
  isLoading: boolean
  onStartInterview?: (interview: AIInterview) => void
  onPreviewQuestions?: (interview: AIInterview) => void
  onDelete?: (interview: AIInterview) => void
  onReanalyzeSuccess?: () => void
  isAdmin?: boolean
  // Added 2026-10-07 — gates Preview & Edit Questions, Retry Processing,
  // and Reanalyze (interviews.generate), and Start Interview
  // (interviews.create). Previously these 4 row actions had no capability
  // check at all, unlike Delete (isAdmin, above). Both default false, same
  // as isAdmin, so an older caller gets the safe/hidden state.
  canManageContent?: boolean
  canCreateInterview?: boolean
  emptyMessage?: string
}

const STATUS_CONFIG: Record<AIInterviewStatus, { label: string; className: string; icon: React.ComponentType<{ className?: string }> }> = {
  pending: {
    label: 'Generating Questions',
    className: 'bg-yellow-100 text-yellow-800',
    icon: ClockIcon,
  },
  questions_generated: {
    label: 'Questions Ready',
    className: 'bg-blue-100 text-blue-800',
    icon: MicrophoneIcon,
  },
  in_progress: {
    label: 'In Progress',
    className: 'bg-purple-100 text-purple-800',
    icon: PlayIcon,
  },
  audio_uploaded: {
    label: 'Audio Uploaded',
    className: 'bg-indigo-100 text-indigo-800',
    icon: MicrophoneIcon,
  },
  transcribed: {
    label: 'Transcribed',
    className: 'bg-cyan-100 text-cyan-800',
    icon: CheckCircleIcon,
  },
  analyzed: {
    label: 'Analyzed',
    className: 'bg-green-100 text-green-800',
    icon: CheckCircleIcon,
  },
  completed: {
    label: 'Completed',
    className: 'bg-green-100 text-green-800',
    icon: CheckCircleIcon,
  },
  failed: {
    label: 'Failed',
    className: 'bg-red-100 text-red-800',
    icon: ExclamationCircleIcon,
  },
}

const TYPE_CONFIG: Record<AIInterviewType, { label: string; className: string; skillInfo?: string }> = {
  ai_interview: {
    label: 'AI Interview',
    className: 'bg-blue-100 text-blue-800',
  },
  express: {
    label: 'Express',
    className: 'bg-orange-100 text-orange-800',
    skillInfo: '1 skill',
  },
  standard: {
    label: 'Standard',
    className: 'bg-purple-100 text-purple-800',
    skillInfo: '', // Will be set dynamically based on selected_skills
  },
}

function getInterviewTypeConfig(
  type: AIInterviewType | string, 
  selectedSkills?: any[]
): { label: string; className: string; skillInfo?: string } {
  const config = TYPE_CONFIG[type as AIInterviewType] || {
    label: type || 'AI Interview',
    className: 'bg-gray-100 text-gray-800',
  }
  
  // For standard interviews, dynamically set skill count
  if (type === 'standard' && selectedSkills && selectedSkills.length > 0) {
    return {
      ...config,
      skillInfo: `${selectedSkills.length} skill${selectedSkills.length !== 1 ? 's' : ''}`,
    }
  }
  
  return config
}

function formatDate(dateString: string): string {
  const date = new Date(dateString)
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

export default function InterviewListView({
  interviews,
  isLoading,
  onStartInterview,
  onPreviewQuestions,
  onDelete,
  onReanalyzeSuccess,
  isAdmin = false,
  canManageContent = false,
  canCreateInterview = false,
  emptyMessage = 'No interviews found',
}: InterviewListViewProps) {
  const [downloadingId, setDownloadingId] = useState<string | null>(null)
  const [reanalyzingId, setReanalyzingId] = useState<string | null>(null)

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
        <span className="ml-2.5 text-sm text-gray-600">Loading interviews...</span>
      </div>
    )
  }

  if (interviews.length === 0) {
    return (
      <div className="text-center py-8">
        <MicrophoneIcon className="mx-auto h-8 w-8 text-gray-400" />
        <h3 className="mt-2 text-sm font-medium text-gray-900">No Interviews</h3>
        <p className="mt-1 text-xs text-gray-500">{emptyMessage}</p>
      </div>
    )
  }

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Interview ID
            </th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Candidate
            </th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Job
            </th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Email
            </th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Phone
            </th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Type
            </th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Status
            </th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Score
            </th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Created On
            </th>
            <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
              Actions
            </th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {interviews.map((interview) => {
            const statusConfig = STATUS_CONFIG[interview.status]
            const StatusIcon = statusConfig.icon
            const typeConfig = getInterviewTypeConfig(interview.interview_type, interview.selected_skills)

            return (
              <tr key={interview.id} className="hover:bg-gray-50">
                <td className="px-4 py-4 whitespace-nowrap">
                  <Link
                    to={`/interviews/${interview.id}/results`}
                    className="text-sm font-medium text-blue-600 hover:text-blue-900 hover:underline"
                    title="View Interview Details"
                  >
                    {interview.session_id}
                  </Link>
                </td>
                <td className="px-4 py-4 whitespace-nowrap">
                  <div>
                    <div className="text-sm font-medium text-gray-900">
                      {interview.candidate_name}
                    </div>
                    <div className="text-xs text-gray-400">
                      {interview.candidate_id}
                    </div>
                  </div>
                </td>
                <td className="px-4 py-4 whitespace-nowrap">
                  <div>
                    <div className="text-sm font-medium text-gray-900">
                      {interview.job_title}
                    </div>
                    <div className="text-xs text-gray-400">
                      {interview.job_code || '-'}
                    </div>
                  </div>
                </td>
                <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                  {interview.candidate_email || '-'}
                </td>
                <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                  {interview.candidate_phone || '-'}
                </td>
                <td className="px-4 py-4 whitespace-nowrap">
                  <div className="flex flex-col">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${typeConfig.className}`}>
                      {typeConfig.label}
                    </span>
                    {typeConfig.skillInfo && (
                      <span className="text-xs text-gray-400 mt-0.5">
                        {typeConfig.skillInfo}
                      </span>
                    )}
                  </div>
                </td>
                <td className="px-4 py-4 whitespace-nowrap">
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusConfig.className}`}>
                    <StatusIcon className="w-3.5 h-3.5 mr-1" />
                    {statusConfig.label}
                  </span>
                </td>
                <td className="px-4 py-4 whitespace-nowrap">
                  {interview.status === 'analyzed' || interview.status === 'completed' ? (
                    interview.overall_score !== null && interview.overall_score !== undefined ? (
                      <div className="flex items-center">
                        <span className={`text-sm font-semibold ${
                          interview.overall_score >= 70 ? 'text-green-600' :
                          interview.overall_score >= 50 ? 'text-yellow-600' :
                          'text-red-600'
                        }`}>
                          {Math.round(interview.overall_score)}
                        </span>
                        {/* {interview.recommendation && (
                          <span className={`ml-2 inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium ${
                            interview.recommendation === 'yes' ? 'bg-green-100 text-green-800' :
                            interview.recommendation === 'no' ? 'bg-red-100 text-red-800' :
                            'bg-gray-100 text-gray-800'
                          }`}>
                            {interview.recommendation === 'yes' ? '✓' : interview.recommendation === 'no' ? '✗' : '?'}
                          </span>
                        )} */}
                      </div>
                    ) : (
                      <span className="text-sm text-gray-400">-</span>
                    )
                  ) : (
                    <span className="text-sm text-gray-400">-</span>
                  )}
                </td>
                <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                  {formatDate(interview.created_at)}
                </td>
                <td className="px-4 py-4 whitespace-nowrap text-right text-sm font-medium">
                  <div className="flex items-center justify-end space-x-2">
                    {/* Preview Questions button - for Express/Standard with questions ready */}
                    {canManageContent &&
                     (interview.interview_type === 'express' || interview.interview_type === 'standard') &&
                     interview.status === 'questions_generated' &&
                     onPreviewQuestions && (
                      <button
                        onClick={() => onPreviewQuestions(interview)}
                        className="text-purple-600 hover:text-purple-900 p-1.5 rounded-lg hover:bg-purple-50 transition-colors"
                        title="Preview & Edit Questions"
                      >
                        <EyeIcon className="h-5 w-5" />
                      </button>
                    )}

                    {/* Start Interview button - only when questions are ready */}
                    {canCreateInterview && interview.status === 'questions_generated' && onStartInterview && (
                      <button
                        onClick={() => onStartInterview(interview)}
                        className="text-blue-600 hover:text-blue-900 p-1.5 rounded-lg hover:bg-blue-50 transition-colors"
                        title="Start Interview"
                      >
                        <PlayIcon className="h-5 w-5" />
                      </button>
                    )}

                    {/* Retry button - show when interview has failed */}
                    {canManageContent && interview.status === 'failed' && (
                      <button
                        onClick={async () => {
                          if (reanalyzingId === interview.id) return
                          setReanalyzingId(interview.id)
                          try {
                            await aiInterviewsApi.updateStatus(interview.id, 'audio_uploaded')
                            await aiInterviewsApi.processNow(interview.id)
                            toast.success('Interview reprocessing started successfully')
                            onReanalyzeSuccess?.()
                          } catch (error) {
                            console.error('Retry failed:', error)
                            toast.error('Failed to retry interview processing')
                          } finally {
                            setReanalyzingId(null)
                          }
                        }}
                        disabled={reanalyzingId === interview.id}
                        className={`p-1.5 rounded-lg transition-colors ${
                          reanalyzingId === interview.id
                            ? 'text-gray-400 cursor-not-allowed'
                            : 'text-red-600 hover:text-red-900 hover:bg-red-50'
                        }`}
                        title={reanalyzingId === interview.id ? 'Retrying...' : 'Retry Processing'}
                      >
                        <ArrowPathIcon className={`h-5 w-5 ${reanalyzingId === interview.id ? 'animate-spin' : ''}`} />
                      </button>
                    )}

                    {/* Reanalyze button - show when analyzed but score is missing */}
                    {canManageContent &&
                     interview.status === 'analyzed' &&
                     (interview.overall_score === null || interview.overall_score === undefined || interview.overall_score === '') && (
                      <button
                        onClick={async () => {
                          if (reanalyzingId === interview.id) return
                          setReanalyzingId(interview.id)
                          try {
                            await aiInterviewsApi.updateStatus(interview.id, 'audio_uploaded')
                            await aiInterviewsApi.processNow(interview.id)
                            toast.success('Interview reanalysis completed successfully')
                            onReanalyzeSuccess?.()
                          } catch (error) {
                            console.error('Reanalysis failed:', error)
                            toast.error('Failed to start interview reanalysis')
                          } finally {
                            setReanalyzingId(null)
                          }
                        }}
                        disabled={reanalyzingId === interview.id}
                        className={`p-1.5 rounded-lg transition-colors ${
                          reanalyzingId === interview.id
                            ? 'text-gray-400 cursor-not-allowed'
                            : 'text-orange-600 hover:text-orange-900 hover:bg-orange-50'
                        }`}
                        title={reanalyzingId === interview.id ? 'Reanalyzing...' : 'Reanalyze'}
                      >
                        <ArrowPathIcon className={`h-5 w-5 ${reanalyzingId === interview.id ? 'animate-spin' : ''}`} />
                      </button>
                    )}

                    {/* Download Report button - show when analyzed with a valid score */}
                    {interview.status === 'analyzed' &&
                     interview.overall_score !== null && interview.overall_score !== undefined && (
                      <button
                        onClick={async () => {
                          if (downloadingId === interview.id) return // Prevent double-click
                          setDownloadingId(interview.id)
                          try {
                            const response = await apiV2.get(
                              `/ai-interviews/${interview.id}/download-report`,
                              { responseType: 'blob' }
                            )
                            const blob = response.data
                            const url = window.URL.createObjectURL(blob)
                            const a = document.createElement('a')
                            a.href = url
                            a.download = `${interview.candidate_name.replace(/\s+/g, '_')}_AI_Interview_Report_${interview.session_id}.pdf`
                            document.body.appendChild(a)
                            a.click()
                            document.body.removeChild(a)
                            window.URL.revokeObjectURL(url)
                          } catch (error) {
                            console.error('Download failed:', error)
                            alert(error instanceof Error ? error.message : 'Failed to download report')
                          } finally {
                            setDownloadingId(null)
                          }
                        }}
                        disabled={downloadingId === interview.id}
                        className={`p-1.5 rounded-lg transition-colors ${
                          downloadingId === interview.id
                            ? 'text-gray-400 cursor-not-allowed'
                            : 'text-green-600 hover:text-green-900 hover:bg-green-50'
                        }`}
                        title={downloadingId === interview.id ? 'Downloading...' : 'Download Report'}
                      >
                        {downloadingId === interview.id ? (
                          <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                        ) : (
                          <ArrowDownTrayIcon className="h-5 w-5" />
                        )}
                      </button>
                    )}

                    {/* Delete button - admin only */}
                    {isAdmin && onDelete && (
                      <button
                        onClick={() => {
                          if (window.confirm(`Are you sure you want to delete interview ${interview.session_id}? This action cannot be undone.`)) {
                            onDelete(interview)
                          }
                        }}
                        className="text-red-600 hover:text-red-900 p-1.5 rounded-lg hover:bg-red-50 transition-colors"
                        title="Delete Interview"
                      >
                        <TrashIcon className="h-5 w-5" />
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}
